import * as _ from "lodash";

import { Table } from "./table";
import { TableButton } from "./button";
import { TableRow } from "./row";

import {
  TableGroupData,
  TableGroupTotal,
  TableButtonData,
  TableRowSelectableFunction
} from "../table.interface";

export class TableGroup {
  constructor(
    private _table: Table,
    private _groupData: TableGroupData,
    private _key: string,
    private _data: Array<TableRow>,
    private _prevGroup: TableGroup
  ) {
    this._setRowsGroup();
    this._setButton();
    this.update();
  }

  private _label: string;
  private _title: string;
  private _button: TableButton;
  private _totals: Array<TableGroupTotal>;

  private _has_button = false;
  private _all_selected_icon = "check_box_outline_blank";

  public get data(): Array<TableRow> {
    return this.getData();
  }

  public getData(): Array<TableRow> {
    return this._data;
  }

  public getRawData(): Array<any> {
    return this._data.map(item => item.getData());
  }

  public get accessor(): string {
    return this._groupData.accessor;
  }

  public get value(): any {
    return this._key;
  }

  public get label(): string {
    return this._label;
  }

  public get title(): string {
    return this._title;
  }

  public get collapsible(): boolean {
    return _.isBoolean(this._groupData.collapsible)
      ? this._groupData.collapsible
      : true;
  }

  public get show_count(): boolean {
    return _.isBoolean(this._groupData.show_count)
      ? this._groupData.show_count
      : true;
  }

  public get label_cols(): number {
    let label_cols = this.collapsible
      ? this._table.columns.length - 1
      : this._table.columns.length;
    if (this.has_button) {
      label_cols--;
    }
    return label_cols;
  }

  public get count(): number {
    return this.data.length;
  }

  public get count_title(): string {
    switch (this.count) {
      case 0:
        return "Nenhum registro agrupado.";
      case 1:
        return "1 registro agrupado.";
      default:
        return `${this.count} registros agrupados.`;
    }
  }

  public get button(): TableButton {
    return this._button;
  }

  public get totals(): Array<TableGroupTotal> {
    return this._totals;
  }

  public get selectable(): TableRowSelectableFunction | boolean {
    return this._table.selectable;
  }

  public get has_button(): boolean {
    return this._has_button;
  }

  public get all_selected_icon(): string {
    return this._all_selected_icon;
  }

  public getSelectedData(raw_data: boolean = false): Array<any> {
    let data = this.getData();
    if (data.length == 0) {
      return [];
    }

    data = data.filter(item => item.selected);
    return _.flatten(data).map(_item => (raw_data ? _item.getData() : _item));
  }

  public getSelectableData(raw_data: boolean = false): Array<any> {
    let data = this.getData();
    if (data.length == 0) {
      return [];
    }

    data = data.filter(row => row.selectable);
    return _.flatten(data).map(_item => (raw_data ? _item.getData() : _item));
  }

  public hasSelectableRows(): boolean {
    let data = this.getData();
    if (data.length == 0) {
      return false;
    }

    data = data.filter(row => row.selectable);
    return data.length > 0;
  }

  public toggleSelectAll() {
    const selected_count = this.getSelectedData().length;
    const all_selectable_count = this.getSelectableData().length;
    const all_selected = selected_count == all_selectable_count;
    this.getSelectableData().forEach(item =>
      item.setSelected(all_selected ? false : true, true)
    );
    this._table.onSelectionChange.emit(this._table.getSelectedData(true));
  }

  public update() {
    try {
      this._setLabel();
      this._setTitle();
      this._setTotals();
      this._setAttributes();
      this._updateButton();
    } catch (e) {
      console.log(e);
    }
  }

  private _setRowsGroup() {
    this.data.forEach(row => row.setGroup(this));
  }

  private _getAccessorData(): any {
    return _.at(this.getRawData()[0], this.accessor)[0];
  }

  private _setLabel() {
    let label: string;
    if (_.isFunction(this._groupData.format)) {
      label = this._groupData.format(
        this._getAccessorData(),
        this.getRawData()
      );
    } else if (this.value) {
      label = this.value;
    }
    if (!label) {
      label = this._groupData.default_label || "Outros";
    }
    this._label = label;
  }

  private _setTitle() {
    const title = this._groupData.title;
    if (_.isFunction(title)) {
      this._title = (<any>title)(this._getAccessorData(), this.getRawData());
    } else if (_.isString(title)) {
      this._title = <any>this._groupData.title;
    } else {
      this._title = this.label;
    }
  }

  private _setButton() {
    const button: TableButtonData = this._groupData.button;
    if (button) {
      if (!button.mode) {
        button.mode = "raised";
      }
      if (!button.color) {
        button.color = "primary";
      }
      this._button = new TableButton(button, null, null, this);
    }
  }

  private _updateButton() {
    if (this._button) {
      this._button.update();
    }
  }

  private _setTotals() {
    this._totals = [];
    const totals = !_.isEmpty(this._groupData.totals)
      ? this._groupData.totals
      : [];

    if (totals.length == 0) return;

    let colspan = 1;
    let total_cols = 0;
    this._totals = this._table.columns
      .map(column => {
        const total_column = totals.filter(
          total_column => total_column.accessor == column.accessor
        )[0];

        if (total_column) {
          const label = total_column.format
            ? total_column.format(
                this.getRawData(),
                this.getData(),
                this._prevGroup
              )
            : "";

          const title = total_column.title
            ? total_column.title(
                this.getRawData(),
                this.getData(),
                this._prevGroup
              )
            : label.replace(/<(?:.|\n)*?>/gm, "");

          const numeric = _.isBoolean(total_column.numeric)
            ? total_column.numeric
            : column.numeric;

          if (total_column.colspan && total_column.colspan > 1) {
            colspan = total_column.colspan;
          } else if (colspan > 1) {
            colspan--;
          } else {
            colspan = 1;
          }

          total_cols += colspan;

          return {
            label,
            title,
            colspan,
            html: total_column.html,
            numeric
          };
        } else {
          if (total_cols >= this._table.columns.length) {
            return null;
          }

          if (colspan > 1) {
            colspan--;
            return null;
          }

          total_cols++;

          return {
            label: "",
            title: "",
            colspan: 1,
            html: false,
            numeric: column.numeric
          };
        }
      })
      .filter(total => !!total);
  }

  private _setAllSelectedIcon() {
    const selected_count = this.getSelectedData().length;
    const all_selectable_count = this.getSelectableData().length;
    this._all_selected_icon =
      all_selectable_count > 0 && selected_count == all_selectable_count
        ? "check_box"
        : selected_count > 0
        ? "indeterminate_check_box"
        : "check_box_outline_blank";
  }

  private _setAttributes() {
    this._has_button =
      !_.isEmpty(this._button) && this._button.visible ? true : false;
    this._setAllSelectedIcon();
  }
}
